import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlTableState, SavedViews, ExportButton, type CsvColumn } from '../../components/console';
import { message } from 'antd';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Building2,
  Plus,
  Search,
  MoreVertical,
  Users,
  Loader2,
  Ban,
  CheckCircle,
  Eye,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useAppSelector } from '../../app/hooks';
import { selectHasPermission } from '../../features/auth/authSlice';
import {
  listTenants,
  deactivateTenant,
  activateTenant,
  type AdminTenant,
} from '../../services/tenantService';

const planLabel = (p?: string | null) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : '—');

const TENANT_EXPORT_COLUMNS: CsvColumn<AdminTenant>[] = [
  { header: 'Name', key: 'name' },
  { header: 'Slug', key: 'slug' },
  { header: 'Plan', value: (t) => t.subscriptionPlan || '' },
  { header: 'Status', value: (t) => (t.isActive === false ? 'suspended' : 'active') },
  { header: 'Users', value: (t) => t._count?.users ?? '' },
  { header: 'Created', value: (t) => (t.createdAt ? new Date(t.createdAt).toISOString() : '') },
];

export const TenantsPage: React.FC = () => {
  const navigate = useNavigate();
  const canCreate = useAppSelector(selectHasPermission('tenant:create'));
  const canSuspend = useAppSelector(selectHasPermission('tenant:suspend'));

  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Search + status filter persist in the URL (deep-linkable) and drive a
  // server-side query (auth-service /tenants supports search/status/plan).
  const { q, filters, setQ, setFilter } = useUrlTableState(['status', 'plan']);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listTenants({
        page: 1,
        limit: 100,
        search: q || undefined,
        status: (filters.status as 'active' | 'suspended' | 'terminated') || undefined,
        plan: filters.plan || undefined,
      });
      setTenants(Array.isArray(res?.tenants) ? res.tenants : []);
    } catch {
      message.error('Could not load tenants');
    } finally {
      setLoading(false);
    }
  }, [q, filters.status, filters.plan]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = tenants;

  const toggleSuspend = async (t: AdminTenant) => {
    const active = t.isActive ?? true;
    setBusyId(t.id);
    try {
      if (active) await deactivateTenant(t.id);
      else await activateTenant(t.id);
      setTenants((prev) => prev.map((x) => (x.id === t.id ? { ...x, isActive: !active } : x)));
      message.success(`${t.name} ${active ? 'suspended' : 'reactivated'}`);
    } catch {
      message.error('Could not update tenant');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1">
          <h1 className="text-h2 text-foreground">Tenants</h1>
          <p className="text-muted-foreground mt-1">Every company on the KaziPeople platform.</p>
        </div>
        <SavedViews pageKey="tenants" />
        <ExportButton filename="tenants" rows={filtered} columns={TENANT_EXPORT_COLUMNS} />
        {canCreate && (
          <Button onClick={() => navigate('/tenants/onboarding')} className="bg-orange-500 hover:bg-orange-600 text-white">
            <Plus className="w-4 h-4 mr-1" /> New tenant
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center gap-4 flex-wrap">
          <CardTitle className="flex items-center gap-2 flex-1">
            <Building2 className="w-5 h-5" /> All tenants ({filtered.length})
          </CardTitle>
          <select
            value={filters.status ?? ''}
            onChange={(e) => setFilter('status', e.target.value || undefined)}
            className="h-9 rounded-md border border-border bg-card px-3 text-sm text-foreground"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="terminated">Terminated</option>
          </select>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or slug…"
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">No tenants found.</div>
          ) : (
            <div className="divide-y divide-border">
              {filtered.map((t) => {
                const active = t.isActive ?? true;
                return (
                  <div key={t.id} className="py-3 flex items-center gap-4 flex-wrap">
                    <button
                      type="button"
                      onClick={() => navigate(`/tenants/${t.id}`)}
                      className="flex items-center gap-3 flex-1 min-w-[200px] text-left"
                    >
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-[10px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700">
                        <Building2 className="w-5 h-5" />
                      </span>
                      <div>
                        <div className="font-medium text-foreground">{t.name}</div>
                        <div className="text-sm text-muted-foreground">/{t.slug}</div>
                      </div>
                    </button>

                    <Badge variant="outline">{planLabel(t.subscriptionPlan)}</Badge>

                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground min-w-[90px]">
                      <Users className="w-4 h-4" /> {t._count?.users ?? 0} users
                    </div>

                    <Badge className={active ? 'bg-green-100 text-green-700' : 'bg-red-100 dark:bg-red-500/15 text-red-700'}>
                      {active ? 'Active' : 'Suspended'}
                    </Badge>

                    <div className="text-sm text-muted-foreground hidden md:block w-24">{fmtDate(t.createdAt)}</div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" disabled={busyId === t.id}>
                          {busyId === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => navigate(`/tenants/${t.id}`)}>
                          <Eye className="w-4 h-4 mr-2" /> View details
                        </DropdownMenuItem>
                        {canSuspend && (
                          <DropdownMenuItem onClick={() => toggleSuspend(t)} className={active ? 'text-red-600 dark:text-red-400' : 'text-green-700'}>
                            {active ? <Ban className="w-4 h-4 mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                            {active ? 'Suspend' : 'Reactivate'}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantsPage;
